import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordStrength, evaluatePassword } from "@/components/PasswordStrength";

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError("No invite token provided."); return; }
    (async () => {
      try { setInfo((await api.get(`/invites/${token}`)).data); }
      catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, [token]);

  const accept = async () => {
    if (!evaluatePassword(password).valid) return toast.error("Please choose a stronger password (8+ chars, mix of upper, lower, number, symbol)");
    setBusy(true);
    try {
      await api.post("/invites/accept", { token, password });
      toast.success("Account created — please sign in");
      navigate("/login", { replace: true });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8"><ShieldCheck size={28} weight="fill" className="text-primary" /><span className="font-head font-bold text-lg">grantSME</span></div>
        {error ? (
          <div className="bg-card border border-border rounded-md p-6 text-center">
            <p className="text-sm text-alert" data-testid="invite-error">{error}</p>
            <Button className="mt-4" variant="outline" onClick={() => navigate("/login")}>Go to sign in</Button>
          </div>
        ) : !info ? (
          <p className="text-muted-foreground">Loading your invite…</p>
        ) : (
          <div className="bg-card border border-border rounded-md p-6">
            <h2 className="font-head font-bold text-xl">Join {info.org_name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{info.invited_by} invited you as <b>{info.role_label}</b>. Set a password to activate your account.</p>
            <div className="mt-5 space-y-3">
              <div className="space-y-1.5"><Label>Email</Label><Input value={info.email} disabled /></div>
              <div className="space-y-1.5"><Label>Choose a password</Label><Input data-testid="accept-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" /><PasswordStrength value={password} /></div>
              <Button className="w-full" data-testid="accept-submit" onClick={accept} disabled={busy}>{busy ? "Creating…" : "Create account"}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
